import {IEditor, EditContext, RectProps} from '@visactor/vtable-editors';

export interface ImageEditorConfig {
  api?:any;
  apiUrl?: string;
  token?: string;
  maxSize?: number;
  acceptTypes?: string[];
  multiple?: boolean;
  onUpload?: (file: File) => Promise<string>;
}

export class ImageEditor implements IEditor {
  private api:any;
  private apiUrl: string;
  private token: string;
  private editorConfig: ImageEditorConfig;
  private element: HTMLDivElement;
  private container: HTMLElement;
  private successCallback: Function;
  private fileInput: HTMLInputElement;
  private currentValue: string | string[];
  private blobUrls: Set<string> = new Set(); // 统一管理blob URLs

  constructor(editorConfig: ImageEditorConfig = {}) {
    this.editorConfig = {
      maxSize: 10,
      acceptTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      multiple: false,
      ...editorConfig
    };
    
    this.apiUrl = this.editorConfig.apiUrl || '';
    this.token = this.editorConfig.token || '';
    this.api = this.editorConfig.api || null;
  }

  onStart({container, value, referencePosition, endEdit}: EditContext) {
    this.container = container;
    this.successCallback = endEdit;
    this.currentValue = value || (this.editorConfig.multiple ? [] : '');

    this._createEditor();
    this._renderImages();
    
    if (referencePosition?.rect) {
      this.adjustPosition(referencePosition.rect);
    }
  }

  private _createEditor() {
    // 创建编辑器容器
    this.element = document.createElement('div');
    this.element.className = 'vtable-image-editor';
    this.element.style.cssText = `
      position: absolute;
      background: white;
      border: 1px solid #d9d9d9;
      border-radius: 6px;
      padding: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      min-width: 300px;
      max-width: 400px;
      z-index: 1000;
    `;

    // 上传区域
    const uploadArea = this._createUploadArea();
    
    // 预览区域
    const previewArea = document.createElement('div');
    previewArea.className = 'preview-area';
    previewArea.style.cssText = `
      max-height: 200px;
      overflow-y: auto;
      margin-bottom: 8px;
    `;

    // 按钮区域
    const buttonArea = this._createButtonArea();

    this.element.appendChild(uploadArea);
    this.element.appendChild(previewArea);
    this.element.appendChild(buttonArea);
    this.container.appendChild(this.element);
  }

  private _createUploadArea(): HTMLElement {
    const uploadArea = document.createElement('div');
    uploadArea.style.cssText = `
      border: 2px dashed #d9d9d9;
      border-radius: 4px;
      padding: 20px;
      text-align: center;
      cursor: pointer;
      margin-bottom: 8px;
    `;
    uploadArea.innerHTML = `
      <div style="color: #666; font-size: 14px;">
        📷 点击或拖拽图片到此处
      </div>
    `;

    // 文件输入
    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.accept = this.editorConfig.acceptTypes?.join(',') || 'image/*';
    this.fileInput.multiple = this.editorConfig.multiple || false;
    this.fileInput.style.display = 'none';
    uploadArea.appendChild(this.fileInput);

    // 事件绑定
    uploadArea.addEventListener('click', () => this.fileInput.click());
    this.fileInput.addEventListener('change', (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      this._handleFiles(files);
    });

    return uploadArea;
  }

  private _createButtonArea(): HTMLElement {
    const buttonArea = document.createElement('div');
    buttonArea.style.cssText = `
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    `;

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = '取消';
    cancelBtn.style.cssText = `
      padding: 4px 12px;
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      background: white;
      cursor: pointer;
    `;

    const confirmBtn = document.createElement('button');
    confirmBtn.textContent = '确定';
    confirmBtn.style.cssText = `
      padding: 4px 12px;
      border: 1px solid #1890ff;
      border-radius: 4px;
      background: #1890ff;
      color: white;
      cursor: pointer;
    `;

    cancelBtn.addEventListener('click', () => this.successCallback(false));
    confirmBtn.addEventListener('click', () => this.successCallback(true));

    buttonArea.appendChild(cancelBtn);
    buttonArea.appendChild(confirmBtn);
    return buttonArea;
  }

  private async _handleFiles(files: File[]) {
    for (const file of files) {
      if (!this._validateFile(file)) continue;

      try {
        const imageUrl = await this._uploadFile(file);
        if (this.editorConfig.multiple) {
          if (!Array.isArray(this.currentValue)) {
            this.currentValue = [];
          }
          this.currentValue.push(imageUrl);
        } else {
          this.currentValue = imageUrl;
        }
        this._renderImages();
      } catch (error) {
        console.error('上传失败:', error);
      }
    }
  }

  private _validateFile(file: File): boolean {
    const maxSize = (this.editorConfig.maxSize || 10) * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`文件大小不能超过 ${this.editorConfig.maxSize}MB`);
      return false;
    }
    return true;
  }

  private async _uploadFile(file: File): Promise<string> {
    if (this.editorConfig.onUpload) {
      return await this.editorConfig.onUpload(file);
    }

    // 默认转换为base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private _renderImages() {
    const previewArea = this.element.querySelector('.preview-area') as HTMLElement;
    if (!previewArea) return;

    previewArea.innerHTML = '';
    
    const images = Array.isArray(this.currentValue) 
      ? this.currentValue 
      : this.currentValue ? [this.currentValue] : [];

    images.forEach((imageUrl, index) => {
      this._createImageItem(imageUrl, index, previewArea);
    });
  }

  private async _createImageItem(imageUrl: string, index: number, container: HTMLElement) {
    const imageItem = document.createElement('div');
    imageItem.style.cssText = `
      display: flex;
      align-items: center;
      padding: 4px;
      margin-bottom: 4px;
      border: 1px solid #f0f0f0;
      border-radius: 4px;
    `;

    const img = document.createElement('img');
    img.style.cssText = `
      width: 40px;
      height: 40px;
      object-fit: cover;
      border-radius: 4px;
      margin-right: 8px;
      background-color: #f5f5f5;
    `;

    // 简化的图片源处理
    try {
      const finalSrc = await this._resolveImageSrc(imageUrl);
      img.src = finalSrc;
      img.onload = () => img.style.backgroundColor = 'transparent';
    } catch (error) {
      // 错误处理：显示错误图标
      img.style.backgroundColor = '#ffebee';
      img.style.color = '#f44336';
      img.alt = '加载失败';
      img.title = `图片加载失败: ${error}`;
    }

    const fileName = document.createElement('span');
    fileName.textContent = `图片 ${index + 1}`;
    fileName.style.cssText = `
      flex: 1;
      font-size: 12px;
      color: #666;
    `;

    const deleteBtn = document.createElement('button');
    deleteBtn.textContent = '×';
    deleteBtn.style.cssText = `
      width: 20px;
      height: 20px;
      border: none;
      background: #ff4d4f;
      color: white;
      border-radius: 50%;
      cursor: pointer;
      font-size: 12px;
    `;

    deleteBtn.addEventListener('click', () => this._deleteImage(index));

    imageItem.appendChild(img);
    imageItem.appendChild(fileName);
    imageItem.appendChild(deleteBtn);
    container.appendChild(imageItem);
  }

  /**
   * 简化的图片源解析 - 核心逻辑
   */
  private async _resolveImageSrc(imageUrl: string): Promise<string> {
    
    // base64 或 blob URL 直接返回
    if (imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')) {
      return imageUrl;
    }

    // 需要认证的API图片
    if (this.apiUrl && this.token) {
      return await this._fetchWithAuth(imageUrl);
    }

    // 普通图片直接返回
    return imageUrl;
  }

  /**
   * 认证获取图片 - 简化版本
   */
  private async _fetchWithAuth(imageUrl: string): Promise<string> {
    const fullUrl = `${this.apiUrl}/assets/${imageUrl}`;
    
    const response = await fetch(fullUrl, {
      headers: { 'Authorization': `Bearer ${this.token}` }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    
    // 统一管理blob URL
    this.blobUrls.add(blobUrl);
    return blobUrl;
  }

  private _deleteImage(index: number) {
    if (Array.isArray(this.currentValue)) {
      this.currentValue.splice(index, 1);
    } else {
      this.currentValue = '';
    }
    this._renderImages();
  }

  adjustPosition(rect: RectProps) {
    this.element.style.top = rect.top + 'px';
    this.element.style.left = rect.left + 'px';
  }

  getValue() {
    return this.currentValue;
  }

  onEnd() {
    // 清理所有blob URLs
    this.blobUrls.forEach(url => URL.revokeObjectURL(url));
    this.blobUrls.clear();
    
    if (this.element && this.container.contains(this.element)) {
      this.container.removeChild(this.element);
    }
  }

  isEditorElement(target: HTMLElement): boolean {
    return this.element && this.element.contains(target);
  }
}